import { checkVisibility, matchElementRange, ViewMode } from '@playbooks/action-cards-utils'
import {
  ElementKind,
  Fragment,
  Question as QuestionDataType,
  Section as SectionDataType,
  Assessment as AssessmentDataType,
  WidgetData,
  Element,
} from '@playbooks/core/src/interfaces/ActionCard'

import Recommendation from '../features/recommendation/Recommendation'
import Question from '../features/question/Question'
import Assessment from '../features/assessment/Assessment'
import { useMemo, useRef } from 'react'
import { SectionContextProvider } from '../contexts/SectionContext'
import ElementWrapper from './ElementWrapper'
import { useAppContext } from '../contexts/AppContext'
import useOnSectionReady from '../hooks/useOnSectionReady'
import { WidgetContext } from '../features/widgets/widgetTypes'
import useScopeSections from '../hooks/useScopeSections'
import Widget from '../features/widgets/widget'
import useMountEffect from '../hooks/useMountEffect'
import tryToPlayFirstVideo from '../utils/tryToPlayFirstVideo'

interface SectionProps {
  data: SectionDataType
}
export default function Section({ data }: SectionProps) {
  const intersectionBarRef = useRef<HTMLDivElement | null>(null)

  const {
    data: actionCardsData,
    actionCardsModel,
    settings: { isWebplay, viewMode },
  } = useAppContext()
  const sectionRef = useOnSectionReady(data)

  const elements = useMemo(() => {
    return data.elements?.filter(element => {
      if (element.kind === ElementKind.RECOMMENDATION) {
        const recommendationFragment = element as Fragment

        return recommendationFragment.hideFromWebPlay && isWebplay
          ? false
          : checkVisibility(element, actionCardsData) &&
              matchElementRange(
                {
                  idOfSep: data.idOfSep,
                  scoreBased: recommendationFragment.scoreBased!,
                  minScore: recommendationFragment.minScore!,
                  maxScore: recommendationFragment.maxScore!,
                  scoringDimensionId: recommendationFragment.scoringDimensionId!,
                },
                actionCardsData,
              )
      }

      return checkVisibility(element, actionCardsData)
    })
  }, [data, actionCardsData, isWebplay])

  const isHeaderLocked = !!actionCardsData.sections.find(sec => sec.id === data.idOfSep)?.isLocked
  const isSectionLocked = actionCardsData.isLocked || isHeaderLocked
  const headerScopeSections = useScopeSections(actionCardsModel, data)

  const widgetsContext = useMemo(() => {
    const widgetElements =
      data.elements?.filter(element => {
        return element.kind === ElementKind.WIDGET
      }) || []

    return {
      allWidgets: [...widgetElements],
      // We set empty texts for widget, as we create
      // this contexts out of a recommendation
      visibleText: '',
      originalText: '',
      section: data,
      headerScopeSections,
    } as WidgetContext
  }, [data, headerScopeSections])

  useMountEffect(() => {
    if (viewMode !== ViewMode.FUNNEL || !isWebplay) return

    // Observe the section container
    // when it enters the viewport, try to autoplay the first video element
    // if this possible (MEI - https://developer.chrome.com/blog/autoplay/)
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            tryToPlayFirstVideo(sectionRef.current as HTMLElement | null | undefined)
          } else {
            const videoElements = sectionRef.current
              ?.querySelector('[data-recommendation-html-container]')
              ?.shadowRoot?.querySelectorAll('video')

            videoElements?.forEach(video => video.pause())

            sectionRef.current
              ?.querySelector('[data-recommendation-html-container]')
              ?.shadowRoot?.querySelectorAll('iframe')
              ?.forEach(iframe => {
                const parent = iframe.parentElement
                const iframeElement = iframe as HTMLIFrameElement
                iframe.remove()
                parent?.appendChild(iframeElement)
              })
          }
        })
      },
      { threshold: 0 },
    )

    if (intersectionBarRef.current) {
      observer.observe(intersectionBarRef.current)
    }

    return () => {
      observer.disconnect()
    }
  })

  return (
    <SectionContextProvider isLocked={isSectionLocked}>
      <div ref={sectionRef} data-section id={`${data.id}|${data.idOfSep}`} className="relative flex flex-col gap-5">
        <div ref={intersectionBarRef} className="absolute top-0 h-full pointer-events-none" />
        {elements?.map(element => {
          switch (element.kind) {
            case ElementKind.RECOMMENDATION: {
              const recommendationFragment = element as Fragment

              return (
                <ElementWrapper
                  key={element.id}
                  id={element.id}
                  locked={isSectionLocked}
                  component={
                    <Recommendation
                      key={recommendationFragment.text}
                      data={recommendationFragment}
                      sectionData={data}
                    />
                  }
                />
              )
            }
            case ElementKind.QUESTION:
              return (
                <ElementWrapper
                  key={element.id}
                  id={element.id}
                  locked={isSectionLocked}
                  component={<Question data={element as QuestionDataType} />}
                />
              )
            case ElementKind.ASSESSMENT:
              return (
                <ElementWrapper
                  key={element.id}
                  id={element.id}
                  locked={isSectionLocked}
                  component={<Assessment data={element as AssessmentDataType} />}
                />
              )
            case ElementKind.WIDGET: {
              const widgetElement = element as WidgetData

              return (
                <ElementWrapper
                  key={element.id}
                  id={element.id}
                  locked={isSectionLocked}
                  component={<Widget key={widgetElement.id} widget={widgetElement} context={widgetsContext} />}
                />
              )
            }
            default:
              return <div>Unsupported element kind: {(element as Element).kind}</div>
          }
        })}
      </div>
    </SectionContextProvider>
  )
}
